import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { AppLanguage, translations, TranslationKey } from './translations';

type I18nContextValue = {
  language: AppLanguage;
  setLanguage: (lang: AppLanguage) => void;
  t: (key: TranslationKey) => string;
};

const defaultI18nContext: I18nContextValue = {
  language: 'en',
  setLanguage: () => {},
  t: (key) => translations.en[key],
};

const I18nContext = createContext<I18nContextValue>(defaultI18nContext);
const LANG_KEY = 'ui.language';

export const I18nProvider = ({ children }: { children: React.ReactNode }) => {
  const [language, setLanguage] = useState<AppLanguage>(() => {
    const saved = localStorage.getItem(LANG_KEY);
    return saved === 'ur' ? 'ur' : 'en';
  });

  useEffect(() => {
    localStorage.setItem(LANG_KEY, language);
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ur' ? 'rtl' : 'ltr';
  }, [language]);

  const value = useMemo<I18nContextValue>(
    () => ({
      language,
      setLanguage,
      t: (key: TranslationKey) => translations[language][key] || translations.en[key] || key,
    }),
    [language],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => useContext(I18nContext);
